// Pure hit-testing for the eraser: which markup item sits under a point.
// Geometry mirrors markupRender — outlines hit near their lines (a rect's
// hollow middle doesn't), text hits on its box.

import { cloudBumpPx, normalizeRect, TEXT_LINE_HEIGHT, wrapTextLines } from './markupRender';
import type { IMarkupPoint, MarkupItem } from './markupTypes';

const px = (p: IMarkupPoint, width: number, height: number) => ({
  x: (p.x / 100) * width,
  y: (p.y / 100) * height,
});

function distToSegment(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lengthSq = abx * abx + aby * aby;
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / lengthSq));
  return Math.hypot(p.x - (a.x + t * abx), p.y - (a.y + t * aby));
}

function nearPolyline(target: { x: number; y: number }, points: IMarkupPoint[], width: number, height: number, tol: number): boolean {
  if (points.length === 1) return Math.hypot(target.x - px(points[0]!, width, height).x, target.y - px(points[0]!, width, height).y) <= tol;
  for (let i = 1; i < points.length; i += 1) {
    if (distToSegment(target, px(points[i - 1]!, width, height), px(points[i]!, width, height)) <= tol) return true;
  }
  return false;
}

/** Rough text width: CJK glyphs run ~1em, Latin ~0.6em — plenty for a hit box. */
function estimatedTextWidthPx(text: string, fontPx: number): number {
  let em = 0;
  for (const ch of text) em += (ch.codePointAt(0) ?? 0) > 0x2e80 ? 1 : 0.6;
  return em * fontPx;
}

/** A selectable box item — a text box. */
export type BoxItem = Extract<MarkupItem, { kind: 'text' }>;

/** A box item's pixel rect. Text: fixed width, height following the wrapped
 *  lines (estimated metrics — precise enough for hits and handles). */
function boxRectPx(
  item: BoxItem,
  width: number,
  height: number,
): { left: number; top: number; w: number; h: number } {
  const w = Math.max(1, (item.w / 100) * width);
  const left = (item.x / 100) * width;
  const top = (item.y / 100) * height;
  const fontPx = Math.max(8, item.size * width);
  const lines = wrapTextLines((s) => estimatedTextWidthPx(s, fontPx), item.text, w);
  return { left, top, w, h: Math.max(1, lines.length) * fontPx * TEXT_LINE_HEIGHT };
}

/** Any item's bounding rect in px — a box uses its box, a stroke the extent
 *  of its points padded by half its line width (clouds add their bump). */
export function itemRectPx(
  item: MarkupItem,
  width: number,
  height: number,
): { left: number; top: number; w: number; h: number } {
  if (item.kind === 'text') return boxRectPx(item, width, height);
  const xs = item.points.map((p) => (p.x / 100) * width);
  const ys = item.points.map((p) => (p.y / 100) * height);
  const linePx = Math.max(1, item.width * width);
  const pad = linePx / 2 + (item.tool === 'cloud' ? cloudBumpPx(linePx, width) : 0);
  const left = Math.min(...xs) - pad;
  const top = Math.min(...ys) - pad;
  return { left, top, w: Math.max(...xs) + pad - left, h: Math.max(...ys) + pad - top };
}

export function hitTestItem(
  item: MarkupItem,
  point: IMarkupPoint,
  width: number,
  height: number,
  tolerancePx: number,
): boolean {
  const target = px(point, width, height);

  if (item.kind === 'text') {
    const box = boxRectPx(item, width, height);
    return (
      target.x >= box.left - tolerancePx &&
      target.x <= box.left + box.w + tolerancePx &&
      target.y >= box.top - tolerancePx &&
      target.y <= box.top + box.h + tolerancePx
    );
  }

  const tol = tolerancePx + (Math.max(1, item.width * width) / 2);

  if (item.tool === 'ellipse') {
    // Hollow like the rect: only the outline hits. No closed form for
    // point-to-ellipse distance — a 24-chord sample of the perimeter is
    // well inside the grab tolerance.
    const r = normalizeRect(item.points[0]!, item.points[1]!);
    const outline: IMarkupPoint[] = [];
    for (let i = 0; i <= 24; i += 1) {
      const a = (i / 24) * Math.PI * 2;
      outline.push({ x: r.x + r.w / 2 + (r.w / 2) * Math.cos(a), y: r.y + r.h / 2 + (r.h / 2) * Math.sin(a) });
    }
    return nearPolyline(target, outline, width, height, tol);
  }

  if (item.tool === 'rect' || item.tool === 'cloud') {
    const r = normalizeRect(item.points[0]!, item.points[1]!);
    const corners: IMarkupPoint[] = [
      { x: r.x, y: r.y }, { x: r.x + r.w, y: r.y },
      { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h },
      { x: r.x, y: r.y },
    ];
    const edgeTol = item.tool === 'cloud' ? tol + cloudBumpPx(Math.max(1, item.width * width), width) : tol;
    return nearPolyline(target, corners, width, height, edgeTol);
  }

  return nearPolyline(target, item.points, width, height, tol);
}

/** The topmost (most recently drawn) item under the point, or null. */
export function topmostHitIndex(
  items: readonly MarkupItem[],
  point: IMarkupPoint,
  width: number,
  height: number,
  tolerancePx: number,
): number | null {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (hitTestItem(items[i]!, point, width, height, tolerancePx)) return i;
  }
  return null;
}
